import React from "react";
import { Link } from "react-router-dom";
import SEOHead from "../../components/tools/SEOHead";
import MarketingShell from "../MarketingShell";

const checks = [
  { name: "Source conversion", score: "100.0%", detail: "Turn source material into citation-ready references." },
  { name: "Streaming marker resolution", score: "100.0%", detail: "Resolve citation markers split across streamed output." },
  { name: "Location metadata fidelity", score: "100.0%", detail: "Preserve PDF page and box, audio time, note line, and web-source locations." },
  { name: "Pre-filter citation grounding", score: "94.4%", detail: "ALCE-inspired check; citation recall 100.0%, precision 83.3%." },
  { name: "Post-filter citation cleanup", score: "100.0%", detail: "Zero false-positive citations in the deliberately injected test cases." },
];

const CitationBenchmarks: React.FC = () => (
  <MarketingShell>
    <SEOHead
      title="YouWo Citation Benchmark | 98.88% Citation Score"
      description="See how YouWo builds and tests source-linked citations across documents, recordings, notes, web pages, streaming, and grounding."
      keywords="AI citation benchmark, citation grounding, ALCE-inspired evaluation, source attribution, YouWo research"
      canonicalUrl="https://youwo.ai/research/citation-benchmarks"
      ogType="article"
      structuredData={{
        "@context": "https://schema.org",
        "@type": "TechArticle",
        headline: "YouWo Citation Benchmark: 98.88% Citation Score",
        description: "How YouWo builds and tests source-linked citations across five checks.",
        datePublished: "2026-09-26",
        author: { "@type": "Organization", name: "YouWo Research" },
        publisher: { "@type": "Organization", name: "YouWo" },
        mainEntityOfPage: "https://youwo.ai/research/citation-benchmarks",
        citation: "https://arxiv.org/abs/2305.14627",
      }}
    />
    <main id="main-content" tabIndex={-1} className="min-h-screen bg-[#f7faff] px-5 pb-24 pt-32 font-roboto text-[#172033] md:pt-40">
      <article className="mx-auto max-w-4xl">
        <nav aria-label="Breadcrumb" className="mb-12 text-sm text-[#52657a]">
          <Link to="/" className="underline underline-offset-4">Home</Link>
          <span aria-hidden="true"> / </span>
          <span>Citation benchmark</span>
        </nav>
        <p className="mb-4 text-xs font-semibold uppercase tracking-[0.16em] text-[#377ab7]">Citation benchmark</p>
        <h1 className="m-0 max-w-3xl text-4xl font-semibold leading-tight tracking-[-0.035em] md:text-6xl">Answers should lead back to evidence.</h1>
        <p className="mt-7 max-w-2xl text-lg leading-8 text-[#52657a]">A useful citation takes you to the page, moment, line, or web source behind an answer. Here is how we build and test that path.</p>

        <section id="results" aria-labelledby="citation-results-title" className="mt-14 border-t border-[#cbd9e8] pt-9">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <h2 id="citation-results-title" className="m-0 text-2xl font-semibold">Citation pipeline checks</h2>
              <p className="mt-2 text-[#52657a]">Five checks, one score.</p>
            </div>
            <div className="text-5xl font-semibold tracking-[-0.05em] text-[#286fa9] md:text-6xl">98.88<span className="text-3xl">%</span></div>
          </div>
          <dl className="mt-10 border-t border-[#dce6ef]">
            {checks.map((check) => (
              <div key={check.name} className="grid gap-1 border-b border-[#dce6ef] py-5 md:grid-cols-[1fr_auto] md:gap-8">
                <div><dt className="font-semibold">{check.name}</dt><p className="mb-0 mt-1 text-sm leading-6 text-[#52657a]">{check.detail}</p></div>
                <dd className="m-0 text-xl font-semibold text-[#286fa9]">{check.score}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section aria-labelledby="citation-method-title" className="mt-14 max-w-3xl">
          <h2 id="citation-method-title" className="text-2xl font-semibold">How we did it</h2>
          <ol className="mt-7 space-y-5 pl-6 leading-7 text-[#52657a]">
            <li><strong className="text-[#172033]">Preserve the source.</strong> Convert PDFs, recordings, notes, and web pages into citation-ready references with their page, box, timestamp, line, or URL.</li>
            <li><strong className="text-[#172033]">Resolve citations as answers stream.</strong> Reassemble markers split across output chunks and attach them to the right source location.</li>
            <li><strong className="text-[#172033]">Check the claim.</strong> Embed cited claims and source snippets in one batch, score their similarity, then measure citation recall and precision before filtering.</li>
            <li><strong className="text-[#172033]">Challenge the filter.</strong> Insert wrong citations deliberately, run the cleanup step, and count those that remain.</li>
          </ol>
          <h3 className="mt-10 text-lg font-semibold">How the score is calculated</h3>
          <p className="leading-7 text-[#52657a]">(100 + 100 + 100 + 94.4 + 100) ÷ 5 = 98.88%. These results come from YouWo's test cases, including business source material. The zero false positives result applies to the injected cases after filtering.</p>
          <p className="text-sm leading-6 text-[#66768a]">The grounding check uses citation precision and recall concepts from <a className="font-semibold text-[#286fa9] underline underline-offset-4" href="https://arxiv.org/abs/2305.14627" target="_blank" rel="noopener noreferrer">ALCE</a>. This is a YouWo citation benchmark, not an official ALCE score or a retrieval-accuracy result. The case set and per-example outputs have not been published.</p>
        </section>
        <div className="mt-14 border-t border-[#cbd9e8] pt-7"><Link to="/research/memory-benchmarks" className="font-semibold text-[#286fa9] underline underline-offset-4">Explore our memory benchmarks</Link></div>
      </article>
    </main>
  </MarketingShell>
);

export default CitationBenchmarks;
